import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Badge,
  Stack,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import CameraIcon from '@mui/icons-material/Camera';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentsIcon from '@mui/icons-material/Payments';
import VerifiedIcon from '@mui/icons-material/Verified';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DoneIcon from '@mui/icons-material/Done';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AnnouncementIcon from '@mui/icons-material/Announcement';

// 引入組件
import CCTVViewer from '../components/CCTVViewer';
import UtilityMeters from '../components/UtilityMeters';
import Notifications from '../components/Notifications';
import SecurityAlerts from '../components/SecurityAlerts';
import NFCPatrol from '../components/NFCPatrol';
import ReportIssue from '../components/ReportIssue';
import WorkOrder from '../components/WorkOrder';
import PaymentManagement from '../components/PaymentManagement';
import AnnouncementCreator from '../components/AnnouncementCreator';

// 儀表板首頁
const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { unreadCount } = useSelector(state => state.notifications);
  const { alerts } = useSelector(state => state.security);
  const { meters } = useSelector(state => state.utilityMeters);
  
  // 從 Redux 獲取付款數據
  const pendingPayments = 12; // 這裡使用模擬數據，實際應用中會從 Redux 獲取
  const verifiedPayments = 83;
  const rejectedPayments = 5;
  
  // 從 Redux 獲取工作訂單數據
  const pendingOrders = 7;
  const inProgressOrders = 4;
  const completedOrders = 93;
  
  // 從 Redux 獲取居民和單位數據
  const totalUnits = 120;
  const occupiedUnits = 112;
  const totalResidents = 325;
  
  const unreadAlerts = alerts?.filter(alert => !alert.read)?.length || 0;
  
  // 導航功能
  const handleNavigate = (path) => {
    navigate(path);
  };
  
  // 獲取儀表板卡片數據
  const getDashboardCards = () => {
    return [
      {
        title: '待審核付款',
        value: pendingPayments,
        icon: <PaymentIcon fontSize="large" color="primary" />,
        action: '審核付款',
        path: '/property-manager/payments',
        color: 'warning.light'
      },
      {
        title: '待處理工單',
        value: pendingOrders + inProgressOrders,
        icon: <BuildIcon fontSize="large" color="error" />,
        action: '管理工單',
        path: '/property-manager/work-orders',
        color: 'error.light'
      },
      {
        title: '安全警報',
        value: unreadAlerts,
        icon: <SecurityIcon fontSize="large" color="warning" />,
        action: '查看警報',
        path: '/property-manager/security',
        color: 'warning.light'
      },
      {
        title: '通告發布',
        icon: <AnnouncementIcon fontSize="large" color="info" />,
        action: '創建通告',
        path: '/property-manager/announcements',
        color: 'info.light'
      }
    ];
  };
  
  // 儀表板統計數據
  const statistics = [
    {
      title: '住戶單位',
      primary: `${occupiedUnits}/${totalUnits}`,
      secondary: `空置率: ${Math.round((1 - occupiedUnits / totalUnits) * 100)}%`,
      icon: <HomeIcon color="primary" />,
    },
    {
      title: '註冊居民',
      primary: totalResidents,
      secondary: `平均每單位 ${(totalResidents / occupiedUnits).toFixed(1)} 人`,
      icon: <PeopleIcon color="secondary" />,
    },
    {
      title: '本月付款',
      primary: `${verifiedPayments + pendingPayments}`,
      secondary: `${verifiedPayments} 已確認, ${pendingPayments} 待處理`,
      icon: <AttachMoneyIcon style={{ color: '#4caf50' }} />,
    }
  ];
  
  // 渲染儀表板卡片
  const renderDashboardCards = () => {
    return getDashboardCards().map((card, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Card 
          sx={{ 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `linear-gradient(45deg, ${card.color || 'primary.light'} 0%, transparent 70%)`,
              opacity: 0.1,
              zIndex: 0
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, zIndex: 1 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="h6" component="div" color="text.secondary">
                {card.title}
              </Typography>
              {card.icon}
            </Box>
            
            {card.value !== undefined && (
              <Typography variant="h4" component="div" sx={{ mb: 2 }}>
                {card.value}
              </Typography>
            )}
            
            <Button 
              variant="outlined" 
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handleNavigate(card.path)}
            >
              {card.action}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    ));
  };
  
  // 渲染統計卡片
  const renderStatistics = () => {
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          大廈概況
        </Typography>
        <Grid container spacing={3}>
          {statistics.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'background.paper', mr: 2 }}>
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                  <Typography variant="h6">
                    {stat.primary}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.secondary}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };
  
  // 渲染最近工單
  const renderRecentWorkOrders = () => {
    // 假設的工單數據
    const recentOrders = [
      { id: 'WO-2023-089', title: '電梯故障', status: 'pending', submittedBy: '王小明', unit: '12A', createdAt: '2023-03-22' },
      { id: 'WO-2023-088', title: '走廊燈不亮', status: 'in-progress', submittedBy: '李小花', unit: '8B', createdAt: '2023-03-21' },
      { id: 'WO-2023-087', title: '垃圾房異味', status: 'pending', submittedBy: '張大勇', unit: '15C', createdAt: '2023-03-21' }
    ];
    
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending': return 'warning';
        case 'in-progress': return 'info';
        case 'completed': return 'success';
        case 'rejected': return 'error';
        default: return 'default';
      }
    };
    
    const getStatusText = (status) => {
      switch (status) {
        case 'pending': return '待處理';
        case 'in-progress': return '處理中';
        case 'completed': return '已完成';
        case 'rejected': return '已拒絕';
        default: return status;
      }
    };
    
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            最近工單
          </Typography>
          <Button 
            size="small" 
            endIcon={<ArrowForwardIcon />}
            onClick={() => handleNavigate('/property-manager/work-orders')}
          >
            查看所有
          </Button>
        </Box>
        
        <List>
          {recentOrders.map((order, index) => (
            <React.Fragment key={order.id}>
              <ListItem alignItems="flex-start">
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'background.paper' }}>
                    <AssignmentIcon color={getStatusColor(order.status)} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography component="span" variant="subtitle2">
                        {order.title}
                      </Typography>
                      <Chip 
                        label={getStatusText(order.status)} 
                        size="small" 
                        color={getStatusColor(order.status)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography variant="body2" component="span" color="text.primary">
                        {order.id}
                      </Typography>
                      {` — ${order.submittedBy} (${order.unit}) · ${order.createdAt}`}
                    </React.Fragment>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="more">
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < recentOrders.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  };
  
  // 渲染儀表板內容
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h1" gutterBottom>
            物業管理儀表板
          </Typography>
        </Grid>
        
        {/* 快速訪問卡片 */}
        {renderDashboardCards()}
        
        {/* 統計信息 */}
        <Grid item xs={12}>
          {renderStatistics()}
        </Grid>
        
        {/* 最近工單 */}
        <Grid item xs={12} md={6}>
          {renderRecentWorkOrders()}
        </Grid>
        
        {/* 公用事業用量 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mt: { xs: 0, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                公用事業用量
              </Typography>
              <Button 
                size="small" 
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate('/property-manager/utilities')}
              >
                查看詳情
              </Button>
            </Box>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'background.paper' }}>
                    <ElectricBoltIcon color="warning" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="電力使用"
                  secondary="較上月減少 5.2%"
                />
                <Typography variant="h6">
                  58,245 kWh
                </Typography>
              </ListItem>
              <Divider variant="inset" component="li" />
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'background.paper' }}>
                    <WaterDropIcon color="info" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="水資源使用"
                  secondary="較上月增加 2.1%"
                />
                <Typography variant="h6">
                  12,765 m³
                </Typography>
              </ListItem>
              <Divider variant="inset" component="li" />
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'background.paper' }}>
                    <LocalFireDepartmentIcon color="error" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="燃氣使用"
                  secondary="較上月增加 0.8%"
                />
                <Typography variant="h6">
                  8,932 m³
                </Typography>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// 物業管理員儀表板
const PropertyManagerDashboard = () => {
  const [currentTab, setCurrentTab] = useState(0);
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ borderRadius: 0 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="管理員儀表板導航"
        >
          <Tab icon={<DashboardIcon />} label="概覽" />
          <Tab icon={<PaymentsIcon />} label="付款管理" />
          <Tab icon={<AssignmentIcon />} label="工單管理" />
          <Tab icon={<CameraIcon />} label="監控系統" />
          <Tab icon={<SecurityIcon />} label="安全警報" />
          <Tab icon={<AnnouncementIcon />} label="通告管理" />
          <Tab icon={<SettingsIcon />} label="系統設置" />
        </Tabs>
      </Paper>
      
      <Box sx={{ mt: 2 }}>
        {currentTab === 0 && <DashboardHome />}
        {currentTab === 1 && <PaymentManagement />}
        {currentTab === 2 && <WorkOrder />}
        {currentTab === 3 && <CCTVViewer />}
        {currentTab === 4 && <SecurityAlerts />}
        {currentTab === 5 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              通告管理
            </Typography>
            <AnnouncementCreator />
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" component="h3" gutterBottom>
              現有通告
            </Typography>
            <Notifications />
          </Box>
        )}
        {currentTab === 6 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5">系統設置</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>系統設置功能正在開發中...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PropertyManagerDashboard; 